import { Button, ButtonVariant } from '@Core/Components'
import { Grid } from '@Core/Components/Grid/Grid'
import { Input } from '@Core/Components/Input/Input'
import { Modal, ModalFooter, ModalSize } from '@Core/Components/Modal/Modal'
import { ModalProps } from '@Core/Components/Modal/Modal.types'
import { PurchasingSupplier } from '@Purchasing/Domain/Model/PurchaseProcess'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const formSchema = z.object({
    corporateName: z.string().min(3, 'Informe a razão social'),
    tradeName: z.string().optional(),
    cnpj: z.string().optional(),
    contactName: z.string().optional(),
    phone: z.string().optional(),
    mail: z
        .string()
        .email('E-mail inválido')
        .or(z.literal(''))
        .optional(),
    paymentTerms: z.string().optional(),
    defaultLeadTimeDays: z.number().int().positive().optional(),
    observation: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

export interface PurchasingSupplierFormValues extends FormValues {}

interface PurchasingSupplierFormModalProps
    extends Omit<ModalProps, 'children'> {
    supplier?: PurchasingSupplier | null
    onSubmit: (values: FormValues) => Promise<unknown> | null
    submitting?: boolean
}

export const PurchasingSupplierFormModal = ({
    isOpen,
    onClose,
    supplier,
    onSubmit,
    submitting,
}: PurchasingSupplierFormModalProps) => {
    const isEdit = Boolean(supplier?._id)

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            corporateName: '',
            tradeName: '',
            cnpj: '',
            contactName: '',
            phone: '',
            mail: '',
            paymentTerms: '',
            observation: '',
        },
    })

    useEffect(() => {
        if (!isOpen) return
        reset({
            corporateName: supplier?.corporateName ?? '',
            tradeName: supplier?.tradeName ?? '',
            cnpj: supplier?.cnpj ?? '',
            contactName: supplier?.contactName ?? '',
            phone: supplier?.phone ?? '',
            mail: supplier?.mail ?? '',
            paymentTerms: supplier?.paymentTerms ?? '',
            defaultLeadTimeDays: supplier?.defaultLeadTimeDays ?? undefined,
            observation: supplier?.observation ?? '',
        })
    }, [isOpen, supplier, reset])

    const submit = handleSubmit(async (values) => {
        const result = await onSubmit(values)
        if (result !== null && result !== undefined) onClose?.()
    })

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Large}>
            <h2 className="text-lg font-bold mb-2">
                {isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </h2>
            <form onSubmit={submit}>
                <Grid container item lg={12}>
                    <Grid item lg={6}>
                        <Input
                            name="corporateName"
                            label="Razão Social *"
                            useUnform={false}
                            register={register('corporateName')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={6}>
                        <Input
                            name="tradeName"
                            label="Nome Fantasia"
                            useUnform={false}
                            register={register('tradeName')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={4}>
                        <Input
                            name="cnpj"
                            label="CNPJ"
                            useUnform={false}
                            register={register('cnpj')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={4}>
                        <Input
                            name="contactName"
                            label="Contato"
                            useUnform={false}
                            register={register('contactName')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={4}>
                        <Input
                            name="phone"
                            label="Telefone"
                            useUnform={false}
                            register={register('phone')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={6}>
                        <Input
                            name="mail"
                            label="E-mail"
                            type="email"
                            useUnform={false}
                            register={register('mail')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={6}>
                        <Input
                            name="paymentTerms"
                            label="Condição de pagamento"
                            useUnform={false}
                            register={register('paymentTerms')}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={6}>
                        <Input
                            name="defaultLeadTimeDays"
                            label="Prazo padrão (dias)"
                            type="number"
                            useUnform={false}
                            register={register('defaultLeadTimeDays', {
                                setValueAs: (v: any) =>
                                    v === '' || v == null ? undefined : Number(v),
                            })}
                            errors={errors}
                        />
                    </Grid>
                    <Grid item lg={12}>
                        <Input
                            name="observation"
                            label="Observações"
                            multiline
                            rows={2}
                            useUnform={false}
                            register={register('observation')}
                            errors={errors}
                        />
                    </Grid>
                </Grid>
                <ModalFooter>
                    <Button
                        type="button"
                        variant={ButtonVariant.secondary}
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant={ButtonVariant.primary}
                        disabled={submitting}
                    >
                        {submitting
                            ? 'Salvando...'
                            : isEdit
                              ? 'Salvar alterações'
                              : 'Cadastrar'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    )
}
